import { useEffect, useState } from 'react';
import {
    Mail, Lock,
} from '@mui/icons-material';

import {
    Divider, Grid, Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import TextField from '../../foundation/TextField';
import Button from '../../foundation/Button/Button';
import Anchor from '../../foundation/Anchor';
import { tryNavigateToRedirectUrl } from '../../../utils/urlUtils';
import { getLoggedUser, loginApi } from '../../../api/auth.api';
import Alert from '../../foundation/Alert';
import { emailPattern } from '../../../utils/forms.utils';
import GoogleLogin from '../../components/GoogleLogin';
import DialogWithHeader from '../../foundation/DialogWithHeader';
import CustomBR from '../../foundation/CustomBR';
import clientConfig from '../../config.client';
import { LoggedInUser } from '../../../../types/auth.types';
import AuthAudienceSelector from '../../components/AuthAudienceSelector';
import GoogleUnverifiedAccountDialog from '../../components/GoogleUnverifiedAccountDialog';
import GoogleRegistrationRequiredDialog from '../../components/GoogleRegistrationRequiredDialog';

export type LoginModalProps = {
    open: boolean
    onClose: () => void
    onResetPasswordClick: () => void
    gotoRegister: () => void
    onLoginComplete?: (user: LoggedInUser) => void | Promise<void>
};

interface IFormLogin {
    username: string;
    password: string;
}

const LoginModal = (props: LoginModalProps) => {
    const [formErr, setFormErr] = useState<string>(null);
    const [googleAuthDialog, setGoogleAuthDialog] = useState<
    'registrationRequired' | 'unverifiedAccount' | null
    >(null);
    const {
        register, handleSubmit, formState: { errors },
    } = useForm<IFormLogin>();

    const onSubmit = async (data: IFormLogin) => {
        try {
            const response = await loginApi(data);
            if (props.onLoginComplete) {
                await props.onLoginComplete(response.data);
            } else {
                tryNavigateToRedirectUrl();
            }
        } catch (error) {
            setFormErr(error.message);
        }
    };

    const onOAuthSuccess = props.onLoginComplete ? async () => {
        try {
            const response = await getLoggedUser();
            await props.onLoginComplete(response.data);
        } catch (error) {
            setFormErr(error.message);
        }
    } : undefined;

    useEffect(() => {
        if (!props.open) {
            setGoogleAuthDialog(null);
        }
    }, [props.open]);

    const renderFooter = () => <Typography variant="body2">
    עוד לא רשום? <Anchor text="הירשם עכשיו" href="#" onClick={ev => {
            ev.preventDefault();
            props.gotoRegister();
        }} /> — זה כיף וקל!
    </Typography>;

    const usernameInput = register('username', { required: true, pattern: emailPattern });
    const passwordInput = register('password', { required: true, minLength: 6 });

    if (googleAuthDialog === 'registrationRequired') {
        return <GoogleRegistrationRequiredDialog
            open={props.open}
            onClose={props.onClose}
            onRegister={props.gotoRegister}
        />;
    }

    if (googleAuthDialog === 'unverifiedAccount') {
        return <GoogleUnverifiedAccountDialog
            open={props.open}
            onClose={props.onClose}
            onLoginWithPassword={() => setGoogleAuthDialog(null)}
            onResetPassword={() => {
                setGoogleAuthDialog(null);
                props.onResetPasswordClick();
            }}
        />;
    }

    return <DialogWithHeader
        renderFooter={renderFooter}
        open={props.open} onClose={props.onClose} title={`התחברות ל-${clientConfig.companyName}`}>
        <div>
            <AuthAudienceSelector onEmployerClick={() => window.location.assign('/backoffice/login')} />
            {formErr && <Alert severity="error">{formErr}</Alert>}
            {formErr && <CustomBR />}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <div>
                        <TextField
                            required
                            {...usernameInput}
                            inputRef={usernameInput.ref}
                            name="username"
                            id="email"
                            label="כתובת אימייל"
                            type="email"
                            fullWidth
                            autoComplete="username"
                            error={!!errors.username}
                            helperText={errors.username && 'כתובת המייל אינה תקינה'}
                            InputProps={{
                                endAdornment: <Mail />,
                            }}
                        />
                    </div>
                    <div>
                        <TextField
                            required
                            {...passwordInput}
                            inputRef={passwordInput.ref}
                            margin="dense"
                            name="password"
                            id="password"
                            label="סיסמא"
                            type="password"
                            autoComplete="current-password"
                            fullWidth
                            error={!!errors.password}
                            helperText={errors.password && 'הסיסמא אינה תקינה, לפחות 6 תווים'}
                            InputProps={{
                                endAdornment: <Lock />,
                            }}
                        />
                    </div>
                    <Grid textAlign="right">
                        <div>
                            <Anchor onClick={ev => {
                                ev.preventDefault();
                                props.onResetPasswordClick();
                            }} href="#" text="שכחתי סיסמא" />
                        </div>
                    </Grid>
                    <CustomBR />
                    <Button onClick={handleSubmit(onSubmit)} color="primary" fullWidth>התחברות</Button>
                </div>
            </form>
            <CustomBR />
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={5}><Divider /></Grid>
                <Grid item><Typography variant="body1">או</Typography></Grid>
                <Grid item xs={5}><Divider /></Grid>
            </Grid>
            <CustomBR />
            <Grid container spacing={3}>
                <Grid sm={12} xs={12} item>
                    <GoogleLogin
                        flow="login"
                        onAuthSuccess={onOAuthSuccess}
                        onRegistrationRequired={() => setGoogleAuthDialog('registrationRequired')}
                        onExistingAccountEmailUnverified={() => setGoogleAuthDialog('unverifiedAccount')}
                    />
                </Grid>
            </Grid>
        </div>
        {/* <DialogBottomStyle>

        </DialogBottomStyle> */}
    </DialogWithHeader>;
};

export default LoginModal;
